import type { DashboardItem } from '@shared/types';

function keyOf(item: DashboardItem): string {
  return `${item.kind}-${item.number}`;
}

/**
 * Resolve a requested item identifier (from `?item=...`) to a real
 * item key, with smart fallback when an issue has been replaced by
 * its bot-created PR.
 *
 * Accepted inputs:
 *  - "issue-11" / "pr-12" — explicit kind+number
 *  - "11" — bare number; tries issue first, then PR by number
 *  - "kb/issue-11" — also accepted (matches PR's head branch)
 */
export function resolveItemKey(
  requested: string,
  items: DashboardItem[],
): string | null {
  // Exact key match wins.
  const direct = items.find((i) => keyOf(i) === requested);
  if (direct) return keyOf(direct);

  // Branch-name pattern: kb/issue-N → resolve to the PR whose head
  // branch matches, OR fall back to the issue itself.
  const branchMatch = requested.match(/^kb\/issue-(\d+)$/);
  if (branchMatch) {
    const n = Number(branchMatch[1]);
    const pr = items.find(
      (i) => i.kind === 'pr' && i.pr?.headBranch === requested,
    );
    if (pr) return keyOf(pr);
    const issue = items.find((i) => i.kind === 'issue' && i.number === n);
    if (issue) return keyOf(issue);
    return null;
  }

  // "issue-N" or "pr-N" — explicit kind.
  const kindMatch = requested.match(/^(issue|pr)-(\d+)$/);
  if (kindMatch) {
    const [, kind, numStr] = kindMatch;
    const n = Number(numStr);
    // If the issue has been replaced by its kb/issue-N PR, follow.
    if (kind === 'issue') {
      const replacementPr = items.find(
        (i) => i.kind === 'pr' && i.pr?.headBranch === `kb/issue-${n}`,
      );
      if (replacementPr) return keyOf(replacementPr);
    }
    const exact = items.find((i) => i.kind === kind && i.number === n);
    if (exact) return keyOf(exact);
    return null;
  }

  // Bare number — try issue, then PR-by-replacement, then PR.
  const bare = requested.match(/^(\d+)$/);
  if (bare) {
    const n = Number(bare[1]);
    const issue = items.find((i) => i.kind === 'issue' && i.number === n);
    if (issue) return keyOf(issue);
    const replacementPr = items.find(
      (i) => i.kind === 'pr' && i.pr?.headBranch === `kb/issue-${n}`,
    );
    if (replacementPr) return keyOf(replacementPr);
    const prByNumber = items.find((i) => i.kind === 'pr' && i.number === n);
    if (prByNumber) return keyOf(prByNumber);
  }

  return null;
}
